/**
 * RoleRoute — wraps a route with three guarantees:
 *  1. User must be authenticated (else redirect to /login).
 *  2. User must belong to an organization (else redirect to /setup).
 *  3. User's role must be in the allowed list (else redirect to /workspace).
 *
 * Usage:
 *   <RoleRoute><Workspace /></RoleRoute>
 *   <RoleRoute roles={['admin', 'auditor']}><AuditPanel /></RoleRoute>
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { resolveRole } from '@/config/roles';

export default function RoleRoute({ children, roles = [] }) {
  const { user, organization, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="role-route-loading">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // No organization yet → force onboarding.  /setup itself is exempt.
  if (!organization && location.pathname !== '/setup') {
    return <Navigate to="/setup" replace />;
  }

  // Once an org exists, block access to /setup (already onboarded).
  if (organization && location.pathname === '/setup') {
    return <Navigate to="/workspace" replace />;
  }

  const currentRole = resolveRole(user);
  if (roles.length > 0 && !roles.includes(currentRole)) {
    return <Navigate to="/workspace" replace />;
  }

  return children;
}
